import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';

import { authToken } from '@/lib/api';

import { authApi } from '../api/auth.api';
import { onSessionChange, refreshSession } from '../session-refresh';
import type { AuthUser } from '../types';
import { AuthContext, type AuthContextValue, type AuthStatus } from './auth-context';

/**
 * Fournit l'état d'authentification et restaure la session au démarrage.
 *
 * Le token d'accès ne vit qu'en mémoire : un rafraîchissement de page le perd.
 * La session survit malgré tout, parce que le refresh token est dans un cookie
 * `httpOnly` que le navigateur renvoie automatiquement. Au montage, on tente
 * donc un échange : s'il aboutit, l'utilisateur reste connecté sans rien saisir.
 *
 * C'est le prix — et la contrepartie — du choix de ne jamais écrire de token
 * dans `localStorage`, où le moindre script injecté pourrait le lire.
 */
export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [user, setUser] = useState<AuthUser | null>(null);

  /**
   * Suit les changements de session, d'où qu'ils viennent.
   *
   * Le rafraîchissement ne part plus seulement d'ici : le client HTTP le
   * déclenche aussi quand une requête se heurte à un 401. Ce fournisseur doit
   * donc **apprendre** l'état de la session plutôt que le provoquer — sans
   * quoi un token renouvelé au fond d'un écran laisserait l'en-tête et la
   * barre latérale dans l'état précédent.
   */
  useEffect(
    () =>
      onSessionChange((session) => {
        if (session) {
          setUser(session.user);
          setStatus('authenticated');
          return;
        }

        setUser(null);
        setStatus('anonymous');
        /* Session perdue : on purge le cache. Sans cela, une reconnexion avec
           un autre compte dans le même onglet réafficherait les données du
           précédent. Le risque de boucle est écarté par le client HTTP, qui
           cesse de tenter un rafraîchissement dès que le token est vidé. */
        queryClient.clear();
      }),
    [queryClient],
  );

  useEffect(() => {
    // `cancelled` évite de poser un état sur un composant démonté. Le double
    // montage du mode strict, lui, est rendu inoffensif par le verrou à un
    // seul appel en vol de `refreshSession` : les deux montages partagent le
    // même échange, donc une seule rotation de jeton côté serveur.
    let cancelled = false;

    refreshSession()
      .then((session) => {
        if (cancelled) return;
        setUser(session.user);
        setStatus('authenticated');
      })
      .catch(() => {
        // Aucune session à restaurer : cas normal d'un premier accès, pas une
        // erreur à remonter à l'utilisateur.
        if (!cancelled) setStatus('anonymous');
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const setSession = useCallback((accessToken: string, nextUser: AuthUser) => {
    authToken.set(accessToken);
    setUser(nextUser);
    setStatus('authenticated');
  }, []);

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch {
      // Le cookie peut déjà être expiré ou révoqué côté serveur. L'utilisateur
      // a demandé à se déconnecter : on vide l'état local quoi qu'il arrive.
    }

    authToken.clear();
    setUser(null);
    setStatus('anonymous');
    // Indispensable : sans purge, les données du compte précédent resteraient
    // affichées si un autre utilisateur se connecte dans le même onglet.
    queryClient.clear();
  }, [queryClient]);

  const value = useMemo<AuthContextValue>(
    () => ({ status, user, setSession, logout }),
    [status, user, setSession, logout],
  );

  return <AuthContext value={value}>{children}</AuthContext>;
};
